import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinanceApiService } from '../finance/finance-api.service';
import { GeographicRouteSelectorComponent } from '../../shared/design-system/geographic-route-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService } from '../catalog/catalog.service';
import {
  CreateFieldSettlementPayload,
  CreateRoutePlanPayload,
  CreateVanStockMovementPayload,
  FieldSettlementRecord,
  RoutePlanRecord,
  SalesApiService,
  VanStockBalanceRecord,
  VisitPlanLinePayload,
  VisitPlanRecord
} from './sales-api.service';

type RouteTab = 'visits' | 'vanStock' | 'settlements';

@Component({
  standalone: true,
  selector: 'ain-sales-distribution-route-detail',
  imports: [
    AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, SelectModule, TableModule,
    CurrencySelectorComponent, JalaliDateInputComponent, GeographicRouteSelectorComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.distribution.routes.form.createTitle') : t('sales.distribution.routes.form.editTitle')" [description]="t('sales.distribution.routes.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card">
          <a class="back-link" [routerLink]="['/sales/distribution-routes']">{{ t('catalog.action.backToList') }}</a>
          @if (isCreateMode()) { <button pButton type="button" [label]="t('common.save')" (click)="save()"></button> }
        </header>
        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.distribution.routes.fields.plannedDate') }}</span><ain-date-input [(ngModel)]="form.plannedDateUtc" /></label>
            <label class="check"><input type="checkbox" [(ngModel)]="form.publishImmediately" /><span>{{ t('sales.distribution.routes.fields.publish') }}</span></label>
            <div class="route-picker">
              <span class="route-picker-label">{{ t('sales.distribution.routes.fields.geography') }}</span>
              <ain-geographic-route-selector [(ngModel)]="form.geographicRouteId" />
            </div>
            <p class="hint">{{ t('sales.distribution.routes.hint.autoVisits') }}</p>
          </section>
        } @else if (route()) {
          <section class="card summary">
            <article><span>{{ t('sales.distribution.routes.fields.geography') }}</span><strong>{{ geographicPath() || '-' }}</strong></article>
            <article><span>{{ t('sales.distribution.routes.fields.routeNumber') }}</span><strong>{{ route()!.routeNumber }}</strong></article>
            <article><span>{{ t('sales.distribution.routes.fields.plannedDate') }}</span><strong>{{ route()!.plannedDateUtc | ainDate }}</strong></article>
            <article><span>{{ t('sales.distribution.routes.fields.visits') }}</span><strong>{{ route()!.completedVisitCount }} / {{ route()!.visitCount }}</strong></article>
            <article><span>{{ t('columns.status') }}</span><strong>{{ routeStatusLabel(route()!.status) }}</strong></article>
          </section>
          <div class="tabs">
            <button type="button" [class.active]="activeTab()==='visits'" (click)="setTab('visits')">{{ t('sales.distribution.routes.tabs.visits') }}</button>
            <button type="button" [class.active]="activeTab()==='vanStock'" (click)="setTab('vanStock')">{{ t('sales.distribution.routes.tabs.vanStock') }}</button>
            <button type="button" [class.active]="activeTab()==='settlements'" (click)="setTab('settlements')">{{ t('sales.distribution.routes.tabs.settlements') }}</button>
          </div>
          @if (activeTab()==='visits') {
            <p-table [value]="visits()" [paginator]="true" [rows]="12">
              <ng-template pTemplate="header">
                <tr><th>#</th><th>{{ t('sales.distribution.visits.fields.customer') }}</th><th>{{ t('columns.status') }}</th><th></th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.sequenceNumber }}</td>
                  <td>{{ customerLabel(row.customerAccountId) }}</td>
                  <td>{{ visitStatusLabel(row.status) }}</td>
                  <td><button pButton type="button" size="small" [label]="t('sales.distribution.visits.open')" (click)="openVisit(row.id)"></button></td>
                </tr>
              </ng-template>
            </p-table>
          }
          @if (activeTab()==='vanStock') {
            <section class="card grid">
              <label>
                <span>{{ t('sales.consignments.fields.offering') }}</span>
                <p-select [options]="offeringOptions()" optionLabel="label" optionValue="value" [(ngModel)]="stockForm.offeringId" [filter]="true" appendTo="body" />
              </label>
              <label><span>{{ t('sales.distribution.vanStock.fields.quantity') }}</span><p-inputNumber [(ngModel)]="stockForm.quantity" /></label>
              <label>
                <span>{{ t('sales.distribution.vanStock.fields.movementType') }}</span>
                <p-select [options]="movementTypes" optionValue="value" [(ngModel)]="stockForm.movementType" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <button pButton type="button" [label]="t('sales.distribution.vanStock.record')" (click)="recordStock()"></button>
            </section>
            <p-table [value]="vanBalances()"><ng-template pTemplate="header"><tr><th>{{ t('sales.consignments.fields.offering') }}</th><th>{{ t('sales.distribution.vanStock.fields.onHand') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ offeringLabel(row.offeringId) }}</td><td>{{ row.quantityOnHand }}</td></tr></ng-template></p-table>
          }
          @if (activeTab()==='settlements') {
            <section class="card grid">
              <label><span>{{ t('sales.distribution.settlements.fields.settlementNumber') }}</span><input pInputText [(ngModel)]="settleForm.settlementNumber" /></label>
              <label><span>{{ t('sales.distribution.settlements.fields.amount') }}</span><p-inputNumber [(ngModel)]="settleForm.amount" [min]="0" /></label>
              <label><span>{{ t('sales.installments.fields.currency') }}</span><ain-currency-selector [(ngModel)]="settleForm.currencyCode" /></label>
              <button pButton type="button" [label]="t('sales.distribution.settlements.create')" (click)="createSettlement()"></button>
            </section>
            <p-table [value]="settlements()">
              <ng-template pTemplate="header"><tr><th>{{ t('sales.distribution.settlements.fields.settlementNumber') }}</th><th>{{ t('sales.distribution.settlements.fields.amount') }}</th><th>{{ t('columns.status') }}</th><th></th></tr></ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.settlementNumber }}</td>
                  <td>{{ row.cashCollectedAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ row.status }}</td>
                  <td>@if (row.status === 'Submitted') { <button pButton type="button" size="small" [label]="t('sales.distribution.settlements.close')" (click)="closeSettlement(row.id)"></button> }</td>
                </tr>
              </ng-template>
            </p-table>
          }
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px}.back-link{color:#1d4ed8;font-weight:600;text-decoration:none}.grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}label{display:grid;gap:6px}.route-picker{grid-column:1/-1;display:grid;gap:8px}.route-picker-label{font-size:13px;color:#475569;font-weight:600}.check{display:flex;align-items:center;gap:8px}.hint{grid-column:1/-1;margin:0;color:#64748b;font-size:13px}.summary{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px}article{display:grid;gap:4px}article span{color:#64748b;font-size:12px}.tabs{display:flex;gap:8px;margin-bottom:10px;flex-wrap:wrap}.tabs button{border:1px solid #cbd5e1;border-radius:999px;padding:6px 12px;cursor:pointer;background:#fff}.tabs button.active{background:#1d4ed8;color:#fff}.error{color:#b91c1c}`]
})
export class SalesDistributionRouteDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly platformApi = inject(FinanceApiService);
  private readonly catalog = inject(CatalogService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly route = signal<RoutePlanRecord | null>(null);
  readonly visits = signal<VisitPlanRecord[]>([]);
  private readonly customerNamesById = signal<Record<string, string>>({});
  readonly vanBalances = signal<VanStockBalanceRecord[]>([]);
  readonly settlements = signal<FieldSettlementRecord[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly activeTab = signal<RouteTab>('visits');
  readonly movementTypes = [
    { value: 'Load', labelKey: 'sales.distribution.vanStock.types.load' },
    { value: 'Sale', labelKey: 'sales.distribution.vanStock.types.sale' },
    { value: 'Adjustment', labelKey: 'sales.distribution.vanStock.types.adjustment' }
  ];
  form = { plannedDateUtc: null as string | null, publishImmediately: true, geographicRouteId: null as string | null };
  readonly geographicPath = signal('');
  stockForm = { offeringId: '', movementType: 'Load', quantity: 10 };
  settleForm = { settlementNumber: '', amount: 0, currencyCode: 'IRR' };

  ngOnInit(): void {
    this.catalog.listOfferings().subscribe({
      next: rows => this.offerings.set(rows),
      error: () => this.offerings.set([])
    });
    const id = this.activatedRoute.snapshot.paramMap.get('routeId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }
    this.loadRoute(id!);
  }

  setTab(tab: RouteTab): void {
    this.activeTab.set(tab);
    const id = this.route()?.id;
    if (!id) return;
    if (tab === 'vanStock') this.loadVanStock(id);
    if (tab === 'settlements') this.loadSettlements(id);
  }

  offeringOptions(): { label: string; value: string }[] {
    return this.offerings().map(item => ({ label: `${item.code} - ${item.title}`, value: item.id }));
  }

  offeringLabel(offeringId: string): string {
    const match = this.offerings().find(item => item.id === offeringId);
    return match ? `${match.code} - ${match.title}` : offeringId;
  }

  loadRoute(id: string): void {
    this.api.getDistributionRoute(id).subscribe({
      next: r => {
        this.route.set(r);
        this.loadGeographicPath(r.geographicRouteId);
        this.api.listRouteVisits(id).subscribe({
          next: v => {
            this.visits.set(v);
            this.loadCustomerNames(v);
            this.loading.set(false);
          },
          error: () => { this.visits.set([]); this.loading.set(false); }
        });
      },
      error: () => { this.errorMessage.set(this.t('sales.distribution.routes.errorLoad')); this.loading.set(false); }
    });
  }

  loadVanStock(routeId: string): void {
    this.api.listVanStock(routeId).subscribe({ next: b => this.vanBalances.set(b) });
  }

  loadSettlements(routeId: string): void {
    this.api.listFieldSettlements(routeId).subscribe({ next: s => this.settlements.set(s) });
  }

  recordStock(): void {
    const routeId = this.route()?.id;
    if (!routeId || !this.stockForm.offeringId) return;
    const payload: CreateVanStockMovementPayload = {
      routePlanId: routeId,
      offeringId: this.stockForm.offeringId,
      movementType: this.stockForm.movementType,
      quantityDelta: this.stockForm.movementType === 'Sale' ? -Math.abs(this.stockForm.quantity) : Math.abs(this.stockForm.quantity)
    };
    this.api.createVanStockMovement(payload).subscribe({
      next: () => this.loadVanStock(routeId),
      error: () => this.errorMessage.set(this.t('sales.distribution.vanStock.error'))
    });
  }

  createSettlement(): void {
    const routeId = this.route()?.id;
    if (!routeId || !this.settleForm.settlementNumber.trim()) return;
    const payload: CreateFieldSettlementPayload = {
      routePlanId: routeId,
      settlementNumber: this.settleForm.settlementNumber.trim(),
      cashCollectedAmount: this.settleForm.amount,
      currencyCode: this.settleForm.currencyCode,
      idempotencyKey: `settle-${Date.now()}`
    };
    this.api.createFieldSettlement(payload).subscribe({
      next: () => this.loadSettlements(routeId),
      error: () => this.errorMessage.set(this.t('sales.distribution.settlements.errorSave'))
    });
  }

  closeSettlement(id: string): void {
    const routeId = this.route()?.id;
    if (!routeId) return;
    this.api.closeFieldSettlement(id).subscribe({ next: () => this.loadSettlements(routeId) });
  }

  save(): void {
    if (!this.form.plannedDateUtc || !this.form.geographicRouteId) {
      this.errorMessage.set(this.t('sales.distribution.routes.errorSave'));
      return;
    }
    const payload: CreateRoutePlanPayload = {
      plannedDateUtc: this.form.plannedDateUtc,
      geographicRouteId: this.form.geographicRouteId,
      publishImmediately: this.form.publishImmediately
    };
    this.api.createDistributionRoute(payload).subscribe({
      next: r => void this.router.navigate(['/sales/distribution-routes', r.id]),
      error: () => this.errorMessage.set(this.t('sales.distribution.routes.errorSave'))
    });
  }

  openVisit(visitId: string): void { void this.router.navigate(['/sales/distribution-visits', visitId]); }

  customerLabel(customerAccountId: string): string {
    const lookup = this.customerNamesById();
    const normalized = customerAccountId.trim().toLowerCase();
    return lookup[normalized] ?? lookup[customerAccountId] ?? customerAccountId;
  }

  routeStatusLabel(status: string): string {
    return this.localizeStatus('sales.distribution.routes.status', status);
  }

  visitStatusLabel(status: string): string {
    return this.localizeStatus('sales.distribution.visits.status', status);
  }

  private loadGeographicPath(routeAreaId?: string | null): void {
    if (!routeAreaId) {
      this.geographicPath.set('');
      return;
    }

    this.platformApi.getGeographicArea(routeAreaId).subscribe({
      next: leaf => {
        this.resolveGeographicBreadcrumb(leaf).then(path => this.geographicPath.set(path));
      },
      error: () => this.geographicPath.set('')
    });
  }

  private async resolveGeographicBreadcrumb(leaf: { id: string; name: string; parentAreaId?: string | null }): Promise<string> {
    const parts: string[] = [leaf.name];
    let parentId = leaf.parentAreaId ?? null;
    while (parentId) {
      const parent = await new Promise<{ id: string; name: string; parentAreaId?: string | null } | null>(resolve => {
        this.platformApi.getGeographicArea(parentId!).subscribe({
          next: resolve,
          error: () => resolve(null)
        });
      });
      if (!parent) {
        break;
      }

      parts.unshift(parent.name);
      parentId = parent.parentAreaId ?? null;
    }

    return parts.join(' › ');
  }

  private loadCustomerNames(visits: VisitPlanRecord[]): void {
    if (visits.length === 0) {
      this.customerNamesById.set({});
      return;
    }

    this.platformApi.listAccountingCustomers().subscribe({
      next: customers => {
        const lookup: Record<string, string> = {};
        for (const customer of customers) {
          const name = customer.displayName?.trim() || customer.code?.trim() || customer.key;
          lookup[customer.key] = name;
          lookup[customer.key.toLowerCase()] = name;
        }

        for (const visit of visits) {
          const id = visit.customerAccountId;
          if (lookup[id] || lookup[id.toLowerCase()]) {
            continue;
          }

          this.platformApi.getPlatformCustomer(id).subscribe({
            next: customer => {
              const name = customer.displayName?.trim() || customer.customerCode?.trim() || id;
              this.customerNamesById.update(current => ({
                ...current,
                [id]: name,
                [id.toLowerCase()]: name
              }));
            }
          });
        }

        this.customerNamesById.set(lookup);
      },
      error: () => this.customerNamesById.set({})
    });
  }

  private localizeStatus(prefix: string, status: string): string {
    if (!status) {
      return '-';
    }

    const key = `${prefix}.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.localization.translate(key);
    return translated === key ? status : translated;
  }

  t(key: string): string { return this.localization.translate(key); }
}
